import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ColorType, createChart, Rectangle } from 'lightweight-charts';
import { useMediaQuery, useTheme, Box, Grid, Typography, Container, Paper, TableContainer, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, IconButton, CircularProgress, keyframes } from '@mui/material';
import DataTable from "./table";
import BasicTable from "./table";
import EnhancedTable from "./table";
import { __fetching_data__ } from '../../components/api.js';
import { useState, useRef } from "react";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { tokens } from "../../theme";
import FontLoader from "../../components/FontLoader";

import { useIsMobile, useIsPortraitTablet, useIsTablet } from '../../components/hooks';

import './index.css';

const base_api_url =
    process.env.NODE_ENV === "production"
        ? window.location.protocol === "https:"
            ? process.env.REACT_APP_API_HTTPS_URL
            : process.env.REACT_APP_API_HTTP_URL
        : "/application";

const scrollbarHideStyle = {
    msOverflowStyle: "none",
    scrollbarWidth: "none",
};

// ================= SKELETON COMPONENTS =================

/** shimmer keyframes — inject once */
const shimmerStyle = `
@keyframes drShimmer {
    0%   { transform: translateX(-100%); }
    100% { transform: translateX(100%); }
}
`;

/** Single chart skeleton (matches bgChart container height) */
function ChartPanelSkeleton({ height = 200, label = "" }) {
    return (
        <div style={{
            width: '100%',
            height,
            borderRadius: 10,
            overflow: 'hidden',
            backgroundColor: '#0d1421',
            position: 'relative',
            flexShrink: 0,
        }}>
            {/* fake grid lines */}
            <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} preserveAspectRatio="none">
                {[...Array(5)].map((_, i) => (
                    <line key={i} x1="0" y1={`${15 + i * 20}%`} x2="100%" y2={`${15 + i * 20}%`} stroke="#1a2744" strokeWidth="1" />
                ))}
                {[...Array(6)].map((_, i) => (
                    <line key={i} x1={`${i * 20}%`} y1="0" x2={`${i * 20}%`} y2="100%" stroke="#1a2744" strokeWidth="1" />
                ))}
            </svg>
            {/* fake wavy line */}
            <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} preserveAspectRatio="none" viewBox="0 0 400 200">
                <polyline
                    points="0,120 40,100 80,130 120,90 160,110 200,80 240,100 280,70 320,95 360,75 400,90"
                    fill="none"
                    stroke="#1e3a5f"
                    strokeWidth="2"
                />
            </svg>
            {/* watermark label */}
            {label && (
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.04)', fontSize: 24, fontWeight: 'bold', pointerEvents: 'none' }}>
                    {label}
                </div>
            )}
            {/* shimmer sweep */}
            <div style={{
                position: 'absolute', inset: 0,
                background: 'linear-gradient(90deg, transparent 0%, rgba(56,189,248,0.07) 40%, rgba(125,211,252,0.14) 50%, rgba(56,189,248,0.07) 60%, transparent 100%)',
                animation: 'drShimmer 1.8s ease-in-out infinite',
            }} />
        </div>
    );
}

/** Skeleton for a full middle-column chart section (3 stacked chart skeletons) */
function MiddleColumnSkeleton({ isMobile, isTablet }) {
    const chartH = (isMobile || isTablet) ? 160 : 200;
    const labels = ['ราคาอ้างอิง', 'PE Ratio', 'Last'];
    return (
        <div style={{ flex: 1, backgroundColor: '#111827', border: '1px solid #1e293b', borderRadius: 12, padding: 16, display: 'flex', flexDirection: 'column', gap: (isMobile || isTablet) ? 5 : 12, overflow: 'hidden', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.3)' }}>
            {labels.map((lbl, i) => (
                <ChartPanelSkeleton key={i} height={chartH} label={lbl} />
            ))}
        </div>
    );
}

/** Skeleton rows inside a region panel */
function RowsSkeleton({ count = 5 }) {
    return (
        <div style={{ padding: '6px', backgroundColor: '#0B1221' }}>
            {[...Array(count)].map((_, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 6px', marginBottom: 2, borderRadius: 6 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#1e293b' }} />
                        <div style={{ width: 36 + (i % 3) * 12, height: 10, borderRadius: 4, backgroundColor: '#1e293b', position: 'relative', overflow: 'hidden' }}>
                            <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg,transparent,rgba(56,189,248,0.1),transparent)', animation: `drShimmer 1.8s ease-in-out ${i * 0.1}s infinite` }} />
                        </div>
                    </div>
                    <div style={{ width: 48, height: 9, borderRadius: 4, backgroundColor: '#1e293b', position: 'relative', overflow: 'hidden' }}>
                        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg,transparent,rgba(56,189,248,0.1),transparent)', animation: `drShimmer 1.8s ease-in-out ${i * 0.12}s infinite` }} />
                    </div>
                </div>
            ))}
        </div>
    );
}

/** Full region-panel skeleton (header + rows) */
function RegionPanelSkeleton({ label = "", rowCount = 5 }) {
    return (
        <div style={{ backgroundColor: '#111827', border: '1px solid rgba(30,41,59,0.8)', borderRadius: 12, overflow: 'hidden', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.3)', display: 'flex', flexDirection: 'column' }}>
            {/* header */}
            <div style={{ padding: '8px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#141b2a', borderBottom: '1px solid rgba(30,41,59,0.6)' }}>
                <div style={{ width: 60, height: 12, borderRadius: 4, backgroundColor: '#1e293b' }} />
                <div style={{ width: 16, height: 16, borderRadius: 4, backgroundColor: '#1e293b' }} />
            </div>
            {/* col labels */}
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 8px', backgroundColor: '#111827', borderBottom: '1px solid rgba(30,41,59,0.6)' }}>
                <div style={{ width: 40, height: 8, borderRadius: 3, backgroundColor: '#1e293b' }} />
                <div style={{ width: 50, height: 8, borderRadius: 3, backgroundColor: '#1e293b' }} />
            </div>
            <RowsSkeleton count={rowCount} />
        </div>
    );
}

/** Asia panel skeleton — matches AsiaPanel layout */
function AsiaPanelSkeleton({ rowCount = 4 }) {
    return (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', backgroundColor: '#111827', border: '1px solid rgba(30,41,59,0.8)', borderRadius: 12, overflow: 'hidden', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.3)', minHeight: 0 }}>
            <div style={{ padding: '6px 10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#141b2a', borderBottom: '1px solid rgba(30,41,59,0.6)', flexShrink: 0 }}>
                <div style={{ width: 50, height: 11, borderRadius: 4, backgroundColor: '#1e293b' }} />
                <div style={{ width: 14, height: 14, borderRadius: 3, backgroundColor: '#1e293b' }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 8px', backgroundColor: '#111827', borderBottom: '1px solid rgba(30,41,59,0.6)', flexShrink: 0 }}>
                <div style={{ width: 35, height: 8, borderRadius: 3, backgroundColor: '#1e293b' }} />
                <div style={{ width: 45, height: 8, borderRadius: 3, backgroundColor: '#1e293b' }} />
            </div>
            <div style={{ flex: 1, overflowY: 'auto', backgroundColor: '#0B1221', padding: 4, ...scrollbarHideStyle }}>
                <RowsSkeleton count={rowCount} />
            </div>
        </div>
    );
}

// ================= EachTable =================
const EachTable = ({ RegionData, collapsedTables, toggleTable, onClickTable, backgroundColors }) => {
    const theme = useTheme();
    const blinkAnimation = keyframes`
        0% { background-color: transparent; }
        100% { background-color: #16a34a; }
    `;

    const isMobile = useIsMobile();
    const isTablet = useIsTablet();
    const isPTablet = useIsPortraitTablet();

    const [selectedRow, setSelectedRow] = useState({});
    const [lastSelectedRegion, setLastSelectedRegion] = useState(null);

    const handleKeyPress = (event, items, region) => {
        setSelectedRow((prev) => {
            const currentIndex = prev[region] ?? -1;
            let newIndex = currentIndex;

            if (event.key === 'ArrowDown') {
                newIndex = currentIndex + 1 < items.length ? currentIndex + 1 : currentIndex;
            } else if (event.key === 'ArrowUp') {
                newIndex = currentIndex - 1 >= 0 ? currentIndex - 1 : -1;
            } else if (event.key === 'Enter' && newIndex !== -1) {
                onClickTable(items[newIndex][0]);
            } else if (event.key === 'Escape') {
                return {};
            }

            if (newIndex !== currentIndex && newIndex !== -1) {
                onClickTable(items[newIndex][0]);
            }

            return newIndex === -1 ? {} : { [region]: newIndex };
        });

        setLastSelectedRegion(region);
    };

    const handleBlur = () => {
        setSelectedRow({});
        setLastSelectedRegion(null);
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', width: '100%', gap: '8px' }}>
            {Object.entries(RegionData).map(([region, items]) => (
                <div
                    key={region}
                    style={{
                        backgroundColor: '#111827',
                        border: '1px solid rgba(30,41,59,0.8)',
                        borderRadius: '12px',
                        overflow: 'hidden',
                        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.3)',
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <div style={{ padding: '8px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#141b2a', borderBottom: '1px solid rgba(30,41,59,0.6)' }}>
                        <span style={{ fontWeight: 'bold', fontSize: '13px', color: '#fff' }}>{region}</span>
                        <IconButton onClick={() => toggleTable(region)} size="small" style={{ color: '#94a3b8', padding: '2px' }}>
                            {collapsedTables[region] ? <ExpandMoreIcon fontSize="small" /> : <ExpandLessIcon fontSize="small" />}
                        </IconButton>
                    </div>

                    {!collapsedTables[region] && (
                        <>
                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 8px', fontSize: '9px', color: '#64748b', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em', backgroundColor: '#111827', borderBottom: '1px solid rgba(30,41,59,0.6)' }}>
                                <span>DR/DRx</span>
                                <span>TradingView</span>
                            </div>

                            <div
                                tabIndex={0}
                                onKeyDown={(e) => handleKeyPress(e, items, region)}
                                onBlur={handleBlur}
                                style={{ outline: 'none', backgroundColor: '#0B1221', padding: '6px', overflowY: 'auto', maxHeight: '200px', ...scrollbarHideStyle }}
                            >
                                {items.map((item, index) => {
                                    const isSelected = lastSelectedRegion === region && selectedRow[region] === index;
                                    const isGreen = backgroundColors?.[item[0]] === "green";

                                    return (
                                        <div
                                            key={item[0]}
                                            onClick={() => {
                                                if (selectedRow[region] === index) {
                                                    setSelectedRow({});
                                                    setLastSelectedRegion(null);
                                                } else {
                                                    setSelectedRow({ [region]: index });
                                                    setLastSelectedRegion(region);
                                                    onClickTable(item[0]);
                                                }
                                            }}
                                            style={{
                                                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                                padding: '5px 6px', borderRadius: '6px', cursor: 'pointer', fontSize: '10px', marginBottom: '2px',
                                                backgroundColor: isGreen ? 'rgba(22,163,74,0.3)' : isSelected ? 'rgba(6,182,212,0.2)' : 'transparent',
                                                border: isSelected ? '1px solid rgba(6,182,212,0.5)' : '1px solid transparent',
                                                transition: 'all 0.15s ease',
                                            }}
                                            onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = 'rgba(30,41,59,0.6)'; }}
                                            onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = isGreen ? 'rgba(22,163,74,0.3)' : 'transparent'; }}
                                        >
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <div style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: isGreen ? '#22c55e' : ['#3b82f6','#f97316','#22c55e','#ef4444','#a855f7','#06b6d4','#eab308','#ec4899'][index % 8], flexShrink: 0 }} />
                                                <span style={{ color: '#e2e8f0', fontWeight: 'bold', letterSpacing: '0.05em' }}>{item[1]}</span>
                                            </div>
                                            <Tooltip title={item[4] || ''}>
                                                <span style={{ color: '#64748b', maxWidth: '80px', textAlign: 'right', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item[2]}</span>
                                            </Tooltip>
                                        </div>
                                    );
                                })}
                            </div>
                        </>
                    )}
                </div>
            ))}
        </div>
    );
};

// ================= AsiaPanel =================
const AsiaPanel = ({ region, items, flex, collapsedTables, toggleTable, onClickTable, backgroundColors }) => {
    const [selectedIndex, setSelectedIndex] = useState(-1);

    const handleKeyPress = (e) => {
        if (e.key === 'ArrowDown') setSelectedIndex(i => Math.min(i + 1, items.length - 1));
        else if (e.key === 'ArrowUp') setSelectedIndex(i => Math.max(i - 1, -1));
        else if (e.key === 'Enter' && selectedIndex !== -1) onClickTable(items[selectedIndex][0]);
        else if (e.key === 'Escape') setSelectedIndex(-1);
    };

    const isCollapsed = collapsedTables[region];

    return (
        <div style={{ flex, display: 'flex', flexDirection: 'column', backgroundColor: '#111827', border: '1px solid rgba(30,41,59,0.8)', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.3)', minHeight: 0 }}>
            <div style={{ padding: '6px 10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#141b2a', borderBottom: '1px solid rgba(30,41,59,0.6)', flexShrink: 0 }}>
                <span style={{ fontWeight: 'bold', fontSize: '12px', color: '#fff' }}>{region}</span>
                <IconButton onClick={() => toggleTable(region)} size="small" style={{ color: '#94a3b8', padding: '2px' }}>
                    {isCollapsed ? <ExpandMoreIcon fontSize="small" /> : <ExpandLessIcon fontSize="small" />}
                </IconButton>
            </div>

            {!isCollapsed && (
                <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 8px', fontSize: '9px', color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', backgroundColor: '#111827', borderBottom: '1px solid rgba(30,41,59,0.6)', flexShrink: 0 }}>
                        <span>DR/DRx</span>
                        <span>TradingView</span>
                    </div>
                    <div tabIndex={0} onKeyDown={handleKeyPress} onBlur={() => setSelectedIndex(-1)} style={{ flex: 1, overflowY: 'auto', outline: 'none', backgroundColor: '#0B1221', padding: '4px', ...scrollbarHideStyle }}>
                        {items.map((item, index) => {
                            const isSelected = selectedIndex === index;
                            const isGreen = backgroundColors?.[item[0]] === "green";
                            return (
                                <div
                                    key={item[0]}
                                    onClick={() => { setSelectedIndex(isSelected ? -1 : index); if (!isSelected) onClickTable(item[0]); }}
                                    style={{
                                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                        padding: '4px 6px', borderRadius: '6px', cursor: 'pointer', fontSize: '10px', marginBottom: '2px',
                                        backgroundColor: isGreen ? 'rgba(22,163,74,0.3)' : isSelected ? 'rgba(6,182,212,0.2)' : 'transparent',
                                        border: isSelected ? '1px solid rgba(6,182,212,0.5)' : '1px solid transparent',
                                        transition: 'all 0.15s ease',
                                    }}
                                    onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = 'rgba(30,41,59,0.6)'; }}
                                    onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = isGreen ? 'rgba(22,163,74,0.3)' : 'transparent'; }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <div style={{ width: '5px', height: '5px', borderRadius: '50%', flexShrink: 0, backgroundColor: isGreen ? '#22c55e' : ['#3b82f6','#f97316','#22c55e','#ef4444','#a855f7','#06b6d4','#eab308','#ec4899'][index % 8] }} />
                                        <span style={{ color: '#e2e8f0', fontWeight: 'bold', letterSpacing: '0.05em' }}>{item[1]}</span>
                                    </div>
                                    <Tooltip title={item[4] || ''}>
                                        <span style={{ color: '#64748b', maxWidth: '70px', textAlign: 'right', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '9px' }}>{item[2]}</span>
                                    </Tooltip>
                                </div>
                            );
                        })}
                    </div>
                </>
            )}
        </div>
    );
};

// ================= MAIN PAGE =================
function DRPage() {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const csrfToken = useSelector((state) => state.csrfToken);
    const [symbol, setSymbol] = useState([]);
    let [data, setData] = useState([]);
    const [abc, setAbc] = useState(0);
    const [isLoad, setLoad] = useState(false);
    const [isInitialLoad, setIsInitialLoad] = useState(true); // skeleton untuk load ครั้งแรก
    const [backgroundColors, setBackgroundColors] = useState([]);
    const [collapsedTables, setCollapsedTables] = useState({});
    const [globalFilter, setGlobalFilter] = useState("");

    const isMobile = useIsMobile();
    const isTablet = useIsTablet();
    const isPTablet = useIsPortraitTablet();

    const chartInstancesRef = useRef([]);
    const chartDivRef = useRef(null);

    const toggleTable = (region) => {
        setCollapsedTables((prev) => ({ ...prev, [region]: !prev[region] }));
    };

    useEffect(() => {
        callAPI();
    }, []);

    useEffect(() => {
        if (!isMobile || !isTablet) {
            setCollapsedTables({});
        }
    }, [isMobile, isTablet]);

    useEffect(() => {
        return () => { destroyAllCharts(); };
    }, []);

    async function callAPI() {
        const response = await __fetching_data__({ url: `${base_api_url}/dr_list_symbol`, method: "GET", headers: { "xsrf-token": csrfToken } });
        if (response.status) {
            setData(response.data);
        }
        setIsInitialLoad(false);
    }

    const destroyAllCharts = () => {
        chartInstancesRef.current.forEach(({ chart, resizeObserver, resizeHandler, container }) => {
            try { resizeObserver.unobserve(container); resizeObserver.disconnect(); } catch (e) {}
            try { window.removeEventListener("resize", resizeHandler); } catch (e) {}
            try { chart.remove(); } catch (e) {}
        });
        chartInstancesRef.current = [];
    };

    const clearDiv = () => {
        destroyAllCharts();
        const el = chartDivRef.current;
        if (el) { el.replaceChildren(); }
    };

    function bgChart(name, data, chartName, lineColor, themeOptions) {
        const chartDiv = chartDivRef.current;
        if (!chartDiv) return [null, null];

        const filterContainer = document.createElement('div');
        filterContainer.setAttribute('id', name);
        filterContainer.style.width = "100%";
        filterContainer.style.borderRadius = '10px';
        filterContainer.style.overflow = 'hidden';
        filterContainer.style.height = (isMobile || isTablet) ? "160px" : "200px";
        chartDiv.appendChild(filterContainer);

        const chart = createChart(filterContainer, {
            width: filterContainer.clientWidth,
            height: filterContainer.clientHeight,
            timeScale: { rightOffset: data.length < 10 ? 0 : data.length < 100 ? 0.5 : 5 }
        });

        chart.applyOptions({
            layout: {
                attributionLogo: false,
                background: { type: 'solid', color: themeOptions.background },
                textColor: themeOptions.textColor,
            },
            watermark: { visible: true, fontSize: 24, horzAlign: 'center', vertAlign: 'center', color: 'rgba(255, 255, 255, 0.05)', text: `${chartName}` },
            grid: {
                horzLines: { color: themeOptions.gridColor, visible: true },
                vertLines: { color: themeOptions.gridColor, visible: true },
            },
            crosshair: {
                horzLine: { color: themeOptions.crosshairColor },
                vertLine: { color: themeOptions.crosshairColor },
            },
        });

        const resizeHandler = () => {
            const isActive = chartInstancesRef.current.some(c => c.chart === chart);
            if (!isActive) return;
            try {
                const containerWidth = filterContainer.clientWidth;
                const containerHeight = (isMobile || isPTablet) ? 160 : 200;
                chart.resize(containerWidth, containerHeight);
            } catch (e) {}
        };

        const resizeObserver = new ResizeObserver(resizeHandler);
        resizeObserver.observe(filterContainer);
        window.addEventListener("resize", resizeHandler);
        resizeHandler();

        const lineSeries = chart.addLineSeries({ color: lineColor, priceLineVisible: false });
        lineSeries.setData(data);

        chartInstancesRef.current.push({ chart, resizeObserver, resizeHandler, container: filterContainer });
        return [chart, lineSeries];
    }

    const inList = (data, lst) => {
        for (let i = 0; i < lst.length; i++) {
            if (data == lst[i]['time']) return i;
        }
        return undefined;
    };

    const checkLatest10Data = async (data) => {
        const today = new Date();
        let backgroundStatus = {};
        const latest10 = data.slice(-10);

        await Promise.all(latest10.map(async (item) => {
            const id = item[0];
            try {
                const response = await __fetching_data__({ url: `${base_api_url}/dr_detail_symbol?id=${id}`, method: "GET", headers: { "xsrf-token": csrfToken } });
                if (!response || !response.data || !response.data.chart1 || response.data.chart1.length === 0) {
                    backgroundStatus[id] = "odd-even"; return;
                }
                let oldestDate = response.data.chart1.map(entry => new Date(entry.time)).sort((a, b) => a - b)[0];
                const diffDays = (today - oldestDate) / (1000 * 60 * 60 * 24);
                backgroundStatus[id] = diffDays <= 7 ? "green" : "odd-even";
            } catch (error) {
                console.error(`Error fetching data for id ${id}:`, error);
                backgroundStatus[id] = "odd-even";
            }
        }));

        return backgroundStatus;
    };

    useEffect(() => {
        const fetchData = async () => {
            const result = await checkLatest10Data(data);
            setBackgroundColors(result);
        };
        fetchData();
    }, [data]);

    const onClickTable = async (id) => {
        setLoad(true);
        const response = await __fetching_data__({ url: `${base_api_url}/dr_detail_symbol?id=${id}`, method: "GET", headers: { "xsrf-token": csrfToken } });
        let chartData = [];
        if (response.status) {
            for (let i = 0; i < response.data.chart3.length; i++) {
                const chartItem = response.data.chart3[i];
                const dateTime = chartItem['date'].includes(' ') ? chartItem['date'].split(' ')[0] : chartItem['date'].split('T')[0];
                let dateData;
                if (typeof chartItem['date'] === 'string') {
                    dateData = dateTime.replaceAll('.', '-');
                } else if (chartItem['date'] instanceof Date) {
                    const year = chartItem['date'].getFullYear();
                    const month = String(chartItem['date'].getMonth() + 1).padStart(2, '0');
                    const day = String(chartItem['date'].getDate()).padStart(2, '0');
                    dateData = `${year}-${month}-${day}`;
                } else { continue; }

                let dataInList = inList(dateData, chartData);
                if (dataInList != undefined) {
                    chartData[dataInList]['value'] = Number(chartItem['value']);
                } else {
                    chartData.push({ 'time': dateData, 'value': Number(chartItem['value']) });
                }
            }

            clearDiv();

            function compare(a, b) {
                if (a.time < b.time) return -1;
                if (a.time > b.time) return 1;
                return 0;
            }
            chartData.sort(compare);
            response.data.chart1.sort(compare);
            response.data.chart2.sort(compare);

            const getCommonDates = (chart1, chart2, chart3) => {
                const dates1 = new Set(chart1.map(item => item.time));
                const dates3 = new Set(chart3.map(item => item.time));
                if (!chart2 || chart2.length === 0) {
                    return [...dates1].filter(date => dates3.has(date));
                } else {
                    const dates2 = new Set(chart2.map(item => item.time));
                    return [...dates1].filter(date => dates2.has(date) && dates3.has(date));
                }
            };

            const filterDataByCommonDates = (data, commonDates) => data.filter(item => commonDates.includes(item.time));

            const isChart2Empty = !response.data.chart2 || response.data.chart2.length === 0;
            const commonDates = getCommonDates(response.data.chart1, response.data.chart2, chartData);
            response.data.chart1 = filterDataByCommonDates(response.data.chart1, commonDates);
            if (!isChart2Empty) response.data.chart2 = filterDataByCommonDates(response.data.chart2, commonDates);
            chartData = filterDataByCommonDates(chartData, commonDates);

            const themeOptions = { background: '#0d1421', textColor: '#94a3b8', gridColor: 'rgba(26, 39, 68, 0.4)', crosshairColor: '#3b82f6' };

            var [chart1, mainSeries1] = bgChart('chart1', response.data.chart1, response.data.name.chart1, colors.blueChart[50], themeOptions);
            chart1.timeScale().fitContent();
            var [chart2, mainSeries2] = bgChart('chart2', response.data.chart2, response.data.name.chart2, colors.redChart[300], themeOptions);
            chart2.timeScale().fitContent();
            var [chart3, mainSeries3] = bgChart('chart3', chartData, response.data.name.chart3, colors.greenChart[50], themeOptions);
            chart3.timeScale().fitContent();

            chart1.timeScale().subscribeVisibleLogicalRangeChange(timeRange => {
                chart1.timeScale().setVisibleLogicalRange(timeRange);
                chart2.timeScale().setVisibleLogicalRange(timeRange);
                chart3.timeScale().setVisibleLogicalRange(timeRange);
            });
            chart2.timeScale().subscribeVisibleLogicalRangeChange(timeRange => {
                chart1.timeScale().setVisibleLogicalRange(timeRange);
                chart2.timeScale().setVisibleLogicalRange(timeRange);
                chart3.timeScale().setVisibleLogicalRange(timeRange);
            });
            chart3.timeScale().subscribeVisibleLogicalRangeChange(timeRange => {
                chart1.timeScale().setVisibleLogicalRange(timeRange);
                chart2.timeScale().setVisibleLogicalRange(timeRange);
                chart3.timeScale().setVisibleLogicalRange(timeRange);
            });

            function getCrosshairDataPoint(series, param) {
                if (!param.time) return null;
                return param.seriesData.get(series) || null;
            }

            function syncCrosshair(chart, series, dataPoint) {
                try {
                    if (dataPoint && dataPoint.value != null && dataPoint.time != null) {
                        chart.setCrosshairPosition(dataPoint.value, dataPoint.time, series);
                    } else {
                        chart.clearCrosshairPosition();
                    }
                } catch (error) { console.error("Error in syncCrosshair: ", error); }
            }

            chart1.subscribeCrosshairMove(param => {
                const dataPoint = getCrosshairDataPoint(mainSeries1, param);
                if (dataPoint) { syncCrosshair(chart2, mainSeries2, dataPoint); syncCrosshair(chart3, mainSeries3, dataPoint); }
            });
            chart2.subscribeCrosshairMove(param => {
                const dataPoint = getCrosshairDataPoint(mainSeries2, param);
                syncCrosshair(chart1, mainSeries1, dataPoint); syncCrosshair(chart3, mainSeries3, dataPoint);
            });
            chart3.subscribeCrosshairMove(param => {
                const dataPoint = getCrosshairDataPoint(mainSeries3, param);
                syncCrosshair(chart1, mainSeries1, dataPoint); syncCrosshair(chart2, mainSeries2, dataPoint);
            });

            setLoad(false);
        }
    };

    const categorizeByStaticId = (data) => ({
        "USA": data.filter((item) => item[2].includes("NYSE") || item[2].includes("NASDAQ")),
        "Europe": data.filter((item) => item[2].includes("MIL") || item[2].includes("EURONEXT") || item[2].includes("OMXCOP")),
        "ETC": data.filter((item) => [181, 241, 243].includes(item[0]))
    });

    const categorizeByStaticId2 = (data) => ({
        "Japan": data.filter((item) => item[2].includes("TSE")),
        "Singapore": data.filter((item) => item[2].includes("SGX") && item[0] !== 181),
        "Vietnam": data.filter((item) => item[2].includes("HOSE")),
    });

    const categorizeByStaticId3 = (data) => ({
        "China": data.filter((item) => item[2].includes("HKEX") && ![241, 243].includes(item[0])),
        "Taiwan": data.filter((item) => item[2].includes("TWSE")),
    });

    const groupedDataLeft = categorizeByStaticId(data);
    const groupedDataRight = categorizeByStaticId2(data);
    const groupedDataRight2 = categorizeByStaticId3(data);

    const filterRegionData = (regionData) => {
        if (!globalFilter) return regionData;
        const filtered = {};
        Object.entries(regionData).forEach(([region, items]) => {
            const filteredItems = items.filter(item =>
                item[1]?.toLowerCase().includes(globalFilter.toLowerCase()) ||
                item[2]?.toLowerCase().includes(globalFilter.toLowerCase())
            );
            if (filteredItems.length > 0) filtered[region] = filteredItems;
        });
        return filtered;
    };

    const chartH = (isMobile || isTablet) ? 160 : 200;

    return (
        <div style={{ width: '100%', height: '100vh', backgroundColor: '#0B1221', color: '#fff', padding: '12px', display: 'flex', flexDirection: 'column', fontFamily: 'sans-serif', overflow: 'hidden' }}>

            {/* inject shimmer keyframes once */}
            <style>{shimmerStyle}</style>

            {/* ——— Top Bar ——— */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '24px', marginBottom: '16px', flexShrink: 0 }}>
                <div style={{ backgroundColor: '#111827', border: '1px solid #1e293b', borderRadius: '9999px', padding: '8px 16px', display: 'flex', alignItems: 'center', gap: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' }}>
                    <svg style={{ width: '16px', height: '16px' }} fill="none" stroke="#64748b" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <input
                        type="text"
                        placeholder="Filter symbol..."
                        value={globalFilter}
                        onChange={(e) => setGlobalFilter(e.target.value)}
                        style={{ background: 'transparent', border: 'none', outline: 'none', fontSize: '12px', color: '#cbd5e1', width: '160px' }}
                    />
                </div>
                {[
                    { label: 'ราคาอ้างอิง', color: colors.blueChart?.[50] || '#3b82f6' },
                    { label: 'PE Ratio',    color: colors.redChart?.[300] || '#ef4444' },
                    { label: 'Last',        color: colors.greenChart?.[50] || '#22c55e' },
                ].map(({ label, color }) => (
                    <div key={label} style={{ backgroundColor: '#111827', padding: '8px 20px', borderRadius: '9999px', fontSize: '11px', color: '#94a3b8', border: '1px solid #1e293b', display: 'flex', alignItems: 'center', gap: '12px', whiteSpace: 'nowrap' }}>
                        <span>{label}</span>
                        <div style={{ width: '32px', height: '2px', backgroundColor: color }} />
                    </div>
                ))}
            </div>

            {/* ——— Main Grid ——— */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : isPTablet ? '1fr' : '2fr 8fr 3fr',
                gap: '16px',
                flex: 1,
                minHeight: 0,
                overflowY: isMobile ? 'auto' : 'hidden',
            }}>

                {/* ——— Left Column: USA / Europe / ETC ——— */}
                {(!isMobile && !isPTablet) && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', height: '100%', overflowY: 'auto', ...scrollbarHideStyle }}>
                        {isInitialLoad ? (
                            /* Skeleton for left panel */
                            <>
                                <RegionPanelSkeleton label="USA"    rowCount={6} />
                                <RegionPanelSkeleton label="Europe" rowCount={4} />
                                <RegionPanelSkeleton label="ETC"    rowCount={3} />
                            </>
                        ) : (
                            <EachTable
                                RegionData={filterRegionData(groupedDataLeft)}
                                collapsedTables={collapsedTables}
                                toggleTable={toggleTable}
                                onClickTable={onClickTable}
                                backgroundColors={backgroundColors}
                            />
                        )}
                    </div>
                )}

                {/* ——— Middle Column: Charts ——— */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? '5px' : '12px', height: '100%', minHeight: 0, position: 'relative' }}>

                    {/* Skeleton overlay while clicking a symbol (chart loading) */}
                    {isLoad && (
                        <div style={{ position: 'absolute', inset: 0, zIndex: 20, display: 'flex', flexDirection: 'column', gap: (isMobile || isTablet) ? 5 : 12 }}>
                            <MiddleColumnSkeleton isMobile={isMobile} isTablet={isTablet} />
                        </div>
                    )}

                    <div
                        style={{
                            flex: 1,
                            backgroundColor: '#111827',
                            border: '1px solid #1e293b',
                            borderRadius: '12px',
                            padding: '16px',
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden',
                            boxShadow: '0 4px 6px -1px rgba(0,0,0,0.3)',
                            opacity: isLoad ? 0 : 1,
                            transition: 'opacity 0.3s ease',
                        }}
                    >
                        <div
                            ref={chartDivRef}
                            style={{ width: '100%', flex: 1, display: 'flex', flexDirection: 'column', gap: (isMobile || isTablet) ? '5px' : '12px' }}
                        />

                        {/* Empty state hint */}
                        {chartInstancesRef.current.length === 0 && !isLoad && (
                            <div style={{ display: 'flex', flex: 1, alignItems: 'center', justifyContent: 'center', color: '#475569', fontSize: '14px', pointerEvents: 'none', position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}>
                                Select a symbol from the list to display charts
                            </div>
                        )}
                    </div>
                </div>

                {/* ——— Right Column: Asia ——— */}
                <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, backgroundColor: '#111827', border: '1px solid rgba(30,41,59,0.8)', borderRadius: '12px', padding: '16px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.3)', overflow: 'hidden' }}>
                    {/* Asia Header */}
                    <div style={{ textAlign: 'center', paddingBottom: '12px', marginBottom: '12px', borderBottom: '1px solid rgba(30,41,59,0.6)', flexShrink: 0 }}>
                        <span style={{ fontWeight: 'bold', fontSize: '14px', color: '#fff', letterSpacing: '0.05em' }}>Asia</span>
                    </div>

                    {/* 2-col grid */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', flex: 1, minHeight: 0, overflow: 'hidden' }}>

                        {/* Left sub-col */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', height: '100%', overflow: 'hidden' }}>
                            {isInitialLoad ? (
                                <>
                                    <AsiaPanelSkeleton rowCount={4} />
                                    <AsiaPanelSkeleton rowCount={4} />
                                    <AsiaPanelSkeleton rowCount={3} />
                                </>
                            ) : (
                                [{ key: 'Japan', flex: 1 }, { key: 'Singapore', flex: 1 }, { key: 'Vietnam', flex: 1 }].map(({ key, flex }) => (
                                    <AsiaPanel
                                        key={key} region={key} items={filterRegionData(groupedDataRight)[key] || []}
                                        flex={flex} collapsedTables={collapsedTables} toggleTable={toggleTable}
                                        onClickTable={onClickTable} backgroundColors={backgroundColors}
                                    />
                                ))
                            )}
                        </div>

                        {/* Right sub-col */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', height: '100%', overflow: 'hidden' }}>
                            {isInitialLoad ? (
                                <>
                                    <AsiaPanelSkeleton rowCount={8} />
                                    <AsiaPanelSkeleton rowCount={3} />
                                </>
                            ) : (
                                [{ key: 'China', flex: 3 }, { key: 'Taiwan', flex: 1 }].map(({ key, flex }) => (
                                    <AsiaPanel
                                        key={key} region={key} items={filterRegionData(groupedDataRight2)[key] || []}
                                        flex={flex} collapsedTables={collapsedTables} toggleTable={toggleTable}
                                        onClickTable={onClickTable} backgroundColors={backgroundColors}
                                    />
                                ))
                            )}
                        </div>
                    </div>
                </div>

                {/* ——— Mobile: Left Panel ——— */}
                {(isMobile || isPTablet) && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <div style={{ backgroundColor: '#111827', border: '1px solid rgba(30,41,59,0.8)', borderRadius: '12px', overflow: 'hidden', maxHeight: '35vh', overflowY: 'auto', ...scrollbarHideStyle }}>
                            <div style={{ padding: '8px 12px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#141b2a', borderBottom: '1px solid rgba(30,41,59,0.6)' }}>
                                <span style={{ fontWeight: 'bold', fontSize: '13px', color: '#fff' }}>America / EU</span>
                            </div>
                            <div style={{ padding: '8px' }}>
                                {isInitialLoad ? (
                                    <>
                                        <RegionPanelSkeleton label="USA"    rowCount={4} />
                                        <RegionPanelSkeleton label="Europe" rowCount={3} />
                                    </>
                                ) : (
                                    <EachTable
                                        RegionData={filterRegionData(groupedDataLeft)}
                                        collapsedTables={collapsedTables}
                                        toggleTable={toggleTable}
                                        onClickTable={onClickTable}
                                        backgroundColors={backgroundColors}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                )}

            </div>
        </div>
    );
}

export default DRPage;